import type { AwardCriterionDto, AwardCriterionPatchDto } from "../../autogen/bff-api";

/**
 * Merge-patch replaces the criteria list wholesale, so the client resends all of it on every edit.
 * Existing rows carry their id so the BFF keeps it stable — sub-criteria included, since evaluation
 * scores, criterion rationales and award-letter justifications are all keyed by it. New rows omit the
 * id and the BFF assigns one. The read DTO allows `null` here while the patch does not, so the nulls
 * are collapsed to `undefined` rather than sent as an explicit clear.
 *
 * Every field the criterion holds must be listed here. Inside a list the merge walker replaces elements
 * rather than merging them, so an omitted key is not "unchanged" — it decodes to the target's default
 * and clears the value. Forgetting a field means the next weight keystroke silently wipes it.
 * `awardCriteriaPatch.test.ts` pins this.
 */
export const toCriteriaPatch = (criteria: AwardCriterionDto[]): AwardCriterionPatchDto[] =>
  criteria.map((criterion) => ({
    id: criterion.id ?? undefined,
    type: criterion.type,
    name: criterion.name,
    description: criterion.description,
    weight: criterion.weight,
    commonRationale: criterion.commonRationale,
    subCriteria: (criterion.subCriteria ?? []).map((sub) => ({
      id: sub.id ?? undefined,
      name: sub.name,
      description: sub.description,
      weight: sub.weight,
      answerFormat: sub.answerFormat,
      requiresDocumentation: sub.requiresDocumentation,
    })),
  }));
